// ── THE PAYMENT-TIMING NOTE ON THE CARD PANEL (FOUNDER CHECKOUT REQUEST 2026-10-27) ──────────────────────────────────────
// Rendered by `InpageCardPayment` on the review step, beside (never inside) Stripe's fields. The authoritative price breakdown
// (subtotal, tax, deposit, balance) lives ONLY in the top-right order summary, fed by the same server quote. This block prints NO
// money at all: it says whether the price is confirmed, and when the card is used - from the server's own plan, never inferred:
//   · no plan (card hold): authorised now, charged only once the booking is confirmed;
//   · deposit, new shop (capture on_accept): authorised now, charged only when the shop accepts; balance at the shop;
//   · deposit, known shop (capture at_order): charged now (never called "authorised only"); balance at the shop;
//   · pay at the shop: no card, nothing charged online.
// While the quote is loading or failed, the card cannot be used either way (Pay refuses), and this says so.

import type { CSSProperties } from "react";
import type { DesignQuote } from "@/lib/design-pay";
import { depositTermsLines } from "@/lib/inpage-card";

export type QuoteState = { status: "loading" } | { status: "ok"; quote: DesignQuote } | { status: "failed"; reason: string };

const lead: CSSProperties = { margin: "5px 0 0", fontSize: 12.5, color: "var(--sg-grey-600)" };

/** What happens after this panel — stated on every state, so a failed quote still leaves a path. */
export const NEXT_PAYMENT_STEP =
  "Enter your card, then submit. This places a hold on your card; you are charged only if the booking is confirmed.";

/** M4 · what happens after this panel when the server's plan is pay at the shop: no card, nothing charged online. */
export const PAY_AT_SHOP_NEXT_STEP =
  "Request a reservation. No card needed and nothing to pay online. If the shop accepts, pay the price plus applicable taxes at the shop on arrival.";

/** M4-D · the next step from the server's capture rule: authorised now and charged on acceptance (new shop), or charged now (known shop). */
export function depositNextStep(plan: NonNullable<DesignQuote["deposit"]>): string {
  return `${depositTermsLines(plan)[2]}`;
}

/** The note under the payment heading: the server's plan as when the card is used. No figure; the summary carries the price. */
export function paymentTimingNote(q: QuoteState): string {
  if (q.status !== "ok") return "Payment details will appear here once confirmed. You cannot submit this booking yet.";
  if (q.quote.deposit) return `${depositNextStep(q.quote.deposit)} The balance is paid to the shop on arrival.`;
  if (q.quote.payment) return PAY_AT_SHOP_NEXT_STEP;
  return NEXT_PAYMENT_STEP;
}

export function QuoteBlock({ q, onRetry }: { q: QuoteState; onRetry?: () => void }) {
  return (
    <div data-inpage-card-quote data-inpage-card-quote-status={q.status} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {q.status === "loading" ? (
        <p role="status" aria-busy="true" style={lead}>
          Confirming your price…
        </p>
      ) : null}
      {q.status === "failed" ? (
        <div data-inpage-card-quote-error role="alert" style={{ fontSize: 12.5, color: "#C4271B" }}>
          <p style={{ margin: "0 0 6px" }}>{q.reason}</p>
          {onRetry ? (
            <button type="button" data-inpage-card-quote-retry onClick={onRetry} style={{ border: "1px solid #C4271B", background: "#fff", color: "#C4271B", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Retry price
            </button>
          ) : null}
        </div>
      ) : null}
      <p data-inpage-card-next style={{ ...lead, padding: "10px 12px", background: "var(--sg-grey-50, #F5F7F8)", borderRadius: 8 }}>
        {paymentTimingNote(q)} {q.status === "ok" && !q.quote.payment ? "The price, taxes and any balance are in your order summary." : ""}
      </p>
    </div>
  );
}
